import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Phone, Mail, MessageCircle, Send, ExternalLink } from 'lucide-react';
import { getCompanyInfo, submitInquiry } from '../data/api';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const { settings: waSettings, openChat } = useWhatsApp();
  const [companyInfo, setCompanyInfo] = useState({ phone: '', email: '', address: '', whatsapp: '', whatsappName: '', officeHours: { weekdays: '', saturday: '' } });
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await submitInquiry(formData);
      if (result.status === 'ok') {
        toast({ title: "Consultation Request Sent!", description: "We'll get back to you within 24 hours." });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        toast({ title: "Error", description: "Failed to send. Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    openChat({ page: 'contact', context: 'contact-section' });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600">
            Ready to transform your space? Let's discuss your project
          </p>
        </div>

        {/* Google Map Section with Animated Arrow */}
        <div className="mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-blue-600">
            {/* Map Container */}
            <div className="relative h-[500px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7277449947396!2d103.84485631475394!3d1.3368766990324284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da17771e59b60f%3A0x5f0b1e7b8e8b8e8b!2s114%20Tagore%20Ln%2C%20Singapore%20787547!5e0!3m2!1sen!2ssg!4v1234567890123!5m2!1sen!2ssg"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Yusen Design & Contract Location"
              ></iframe>
              
              {/* Animated Arrow Pointer */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-full pointer-events-none">
                {/* Arrow SVG with Animation */}
                <div className="relative animate-bounce">
                  <svg 
                    width="60" 
                    height="80" 
                    viewBox="0 0 60 80" 
                    className="drop-shadow-2xl"
                  >
                    {/* Arrow shaft */}
                    <line 
                      x1="30" 
                      y1="0" 
                      x2="30" 
                      y2="60" 
                      stroke="#EF4444" 
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    {/* Arrow head */}
                    <polygon 
                      points="30,70 15,50 45,50" 
                      fill="#EF4444"
                    />
                    {/* Glow effect */}
                    <circle 
                      cx="30" 
                      cy="70" 
                      r="8" 
                      fill="#EF4444" 
                      opacity="0.3"
                      className="animate-ping"
                    />
                  </svg>
                  
                  {/* Location Badge */}
                  <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap animate-pulse">
                    <div className="text-xs font-semibold">📍 We're Here!</div>
                    <div className="text-xs">114 Tagore Lane</div>
                  </div>
                </div>
              </div>
              
              {/* Address Overlay Card */}
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-2xl border border-blue-600 max-w-xs">
                <div className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Visit Our Office</h3>
                    <p className="text-sm text-gray-700 mb-2">{companyInfo.address}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>{companyInfo.officeHours.weekdays}</p>
                      <p>{companyInfo.officeHours.saturday}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Phone */}
            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                  <p className="text-gray-600 text-sm mb-2">{companyInfo.phone}</p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Call us now
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                  <p className="text-gray-600 text-sm mb-2">{companyInfo.email}</p>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Send email
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                  <p className="text-gray-600 text-sm mb-1">Chat with {waSettings.contactName} instantly</p>
                  <p className="text-green-600 font-medium text-sm mb-3">+65 {waSettings.phoneNumber.slice(2)}</p>
                  <Button
                    onClick={handleWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Office Address</h3>
                  <p className="text-gray-600 text-sm mb-2">{companyInfo.address}</p>
                  <p className="text-xs text-gray-500 mb-1">{companyInfo.officeHours.weekdays}</p>
                  <p className="text-xs text-gray-500 mb-1">{companyInfo.officeHours.saturday}</p>
                  <p className="text-xs text-gray-500">Sunday: Closed</p>
                </div>
              </CardContent>
            </Card>

            {/* QR Code - Tappable on Mobile */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <h3 className="font-semibold text-gray-900 mb-3">Visit Our Website</h3>
                
                {/* Tappable QR Code */}
                <button
                  onClick={() => window.open('https://yusen-portfolio.preview.emergentagent.com', '_blank', 'noopener,noreferrer')}
                  className="relative group w-36 h-36 sm:w-44 sm:h-44 bg-white rounded-xl p-2 shadow-inner 
                    cursor-pointer transform transition-all duration-200 
                    hover:scale-105 hover:shadow-xl active:scale-95
                    border-2 border-transparent hover:border-blue-500
                    focus:outline-none focus:ring-4 focus:ring-blue-300"
                  data-testid="tappable-qr-code"
                  aria-label="Tap to visit Yusen Design website"
                >
                  <img 
                    src="/yusen-qr-branded-final.png"
                    alt="Tap to visit Yusen Design website"
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Hover/Tap Overlay */}
                  <div className="absolute inset-0 bg-blue-600/90 rounded-xl flex flex-col items-center justify-center
                    opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200">
                    <ExternalLink className="w-8 h-8 text-white mb-2" />
                    <span className="text-white font-semibold text-sm">TAP TO OPEN</span>
                  </div>
                </button>

                {/* Tap Indicator */}
                <div className="mt-3 flex items-center gap-2 text-blue-600">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                  </span>
                  <p className="text-xs font-medium">
                    Tap to visit instantly
                  </p>
                </div>
                
                <p className="text-xs text-gray-400 mt-2">
                  Or scan with your camera
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Book a Free Consultation
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+65 1234 5678"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">Service Type *</Label>
                      <Select onValueChange={handleSelectChange} value={formData.service} required>
                        <SelectTrigger className="border-gray-300 focus:border-blue-600 focus:ring-blue-600">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residential">Residential Design</SelectItem>
                          <SelectItem value="commercial">Commercial Design</SelectItem>
                          <SelectItem value="renovation">Renovation Services</SelectItem>
                          <SelectItem value="consultation">Consultation Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your project, budget, and timeline..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="border-gray-300 focus:border-blue-600 focus:ring-blue-600 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white transform hover:scale-105 transition-all duration-300 shadow-lg shadow-blue-500/50"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {submitting ? 'Sending...' : 'Send Consultation Request'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;